<script setup>
import { computed, onMounted, ref, useTemplateRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import AcessoAPI from 'dashboard/api/acesso';
import { SEM_ACESSO, gruposDoModulo } from './catalogo';
import { fraseDoErro } from './fraseDoErro';

import SettingsLayout from '../SettingsLayout.vue';
import CabecalhoDosPapeis from './components/CabecalhoDosPapeis.vue';
import DialogoDoGrupo from './components/DialogoDoGrupo.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';

// ACESSO-01 (L9a): a aba Grupos de Papéis, como no quadro 5 do mock v3. Os grupos da conta, que se criam, se editam e se
// apagam, e os da Powerbizz, só para ver, módulo a módulo, na ordem do catálogo. O "Sem acesso" de cada módulo não
// entra: é o grupo vazio.
//
// São duas seções, a da conta primeiro (Task 9.6). Numa tabela só, os grupos da conta, os únicos que se mexem, ficavam
// perdidos entre os mais de trinta da Powerbizz, e a coluna "Papéis" ficava vazia neles, porque a API não conta os
// papéis que usam um grupo da Powerbizz. Na seção da Powerbizz, a segunda linha é a frase do grupo, o que ele libera.
const { t } = useI18n();
const store = useStore();
const catalogo = useMapGetter('acesso/catalogo');

const daConta = ref([]);
const carregando = ref(true);

const cabecalhosDaConta = computed(() => [
  t('PAPEIS.GRUPOS.GRUPO'),
  t('PAPEIS.GRUPOS.ESCOPOS'),
  t('PAPEIS.GRUPOS.PAPEIS'),
  t('PAPEIS.GRUPOS.ACOES'),
]);
const cabecalhosDaPowerbizz = computed(() => [
  t('PAPEIS.GRUPOS.GRUPO'),
  t('PAPEIS.GRUPOS.ESCOPOS'),
]);

const modulos = computed(() => catalogo.value?.modulos || []);

const linhasDaConta = computed(() =>
  modulos.value.flatMap(modulo =>
    daConta.value
      .filter(grupo => grupo.modulo === modulo.chave)
      .map(grupo => ({
        chave: `conta:${grupo.id}`,
        modulo,
        nome: grupo.nome,
        escopos: grupo.escopos.length,
        papeis: grupo.papeis.length,
        daConta: grupo,
      }))
  )
);

const linhasDaPowerbizz = computed(() =>
  modulos.value.flatMap(modulo =>
    gruposDoModulo(catalogo.value, modulo.chave)
      .filter(
        grupo => grupo.origem === 'powerbizz' && grupo.chave !== SEM_ACESSO
      )
      .map(grupo => ({
        chave: `powerbizz:${modulo.chave}:${grupo.chave}`,
        modulo,
        nome: grupo.rotulo,
        frase: grupo.frase,
        escopos: grupo.escopos.length,
      }))
  )
);

// O catálogo vem de novo junto: ele leva os grupos da conta ao seletor do editor.
const carregar = async () => {
  carregando.value = true;
  try {
    const [, { data }] = await Promise.all([
      store.dispatch('acesso/carregarCatalogo', { recarregar: true }),
      AcessoAPI.grupos(),
    ]);
    daConta.value = data.grupos;
  } catch (falha) {
    useAlert(fraseDoErro(falha, t('PAPEIS.GRUPOS.ERRO_AO_CARREGAR')));
  } finally {
    carregando.value = false;
  }
};

const dialogoDoGrupo = useTemplateRef('dialogoDoGrupo');

// Apagar confirma no Dialog. O grupo em uso volta 422 `em_uso`, com os papéis que o usam, e a frase fica no Dialog.
const dialogoDeApagar = useTemplateRef('dialogoDeApagar');
const paraApagar = ref(null);
const apagando = ref(false);
const erroAoApagar = ref('');

const pedirParaApagar = grupo => {
  paraApagar.value = grupo;
  erroAoApagar.value = '';
  dialogoDeApagar.value?.open();
};

const apagar = async () => {
  apagando.value = true;
  try {
    await AcessoAPI.apagarGrupo(paraApagar.value.id);
    dialogoDeApagar.value?.close();
    useAlert(t('PAPEIS.GRUPOS.APAGADO'));
    carregar();
  } catch (falha) {
    erroAoApagar.value = fraseDoErro(falha, t('PAPEIS.GRUPOS.ERRO_AO_APAGAR'));
  } finally {
    apagando.value = false;
  }
};

onMounted(carregar);
</script>

<template>
  <SettingsLayout
    :is-loading="carregando"
    :loading-message="t('PAPEIS.CARREGANDO')"
  >
    <template #header>
      <CabecalhoDosPapeis>
        <template #acoes>
          <Button
            :label="t('PAPEIS.GRUPOS.NOVO')"
            icon="i-lucide-plus"
            size="sm"
            data-novo-grupo
            @click="dialogoDoGrupo?.abrir()"
          />
        </template>
      </CabecalhoDosPapeis>
    </template>
    <template #body>
      <section class="flex flex-col pt-2" data-da-conta>
        <h2 class="mb-0 text-heading-2 text-n-slate-12">
          {{ t('PAPEIS.GRUPOS.DA_CONTA') }}
        </h2>
        <BaseTable
          v-if="linhasDaConta.length"
          :headers="cabecalhosDaConta"
          :items="linhasDaConta"
        >
          <template #header-1="{ header }">
            <span class="block text-end">{{ header }}</span>
          </template>
          <template #header-2="{ header }">
            <span class="block text-end">{{ header }}</span>
          </template>
          <template #header-3="{ header }">
            <span class="sr-only">{{ header }}</span>
          </template>
          <template #row="{ items }">
            <BaseTableRow
              v-for="linha in items"
              :key="linha.chave"
              :item="linha"
              :data-grupo="linha.chave"
            >
              <BaseTableCell>
                <span class="text-n-slate-12">
                  {{
                    t('PAPEIS.GRUPOS.NOME', {
                      modulo: linha.modulo.rotulo,
                      nome: linha.nome,
                    })
                  }}
                </span>
              </BaseTableCell>
              <BaseTableCell align="end" class="tabular-nums" data-escopos>
                {{ linha.escopos }}
              </BaseTableCell>
              <BaseTableCell align="end" class="tabular-nums" data-papeis>
                {{ linha.papeis }}
              </BaseTableCell>
              <BaseTableCell align="end">
                <div class="flex justify-end gap-1">
                  <Button
                    v-tooltip.top="
                      t('PAPEIS.GRUPOS.EDITAR', { nome: linha.nome })
                    "
                    :aria-label="
                      t('PAPEIS.GRUPOS.EDITAR', { nome: linha.nome })
                    "
                    icon="i-lucide-pencil"
                    slate
                    ghost
                    sm
                    data-editar
                    @click="dialogoDoGrupo?.abrir({ grupo: linha.daConta })"
                  />
                  <Button
                    v-tooltip.top="
                      t('PAPEIS.GRUPOS.APAGAR', { nome: linha.nome })
                    "
                    :aria-label="
                      t('PAPEIS.GRUPOS.APAGAR', { nome: linha.nome })
                    "
                    icon="i-lucide-trash-2"
                    slate
                    ghost
                    sm
                    class="hover:enabled:text-n-ruby-11"
                    data-apagar
                    @click="pedirParaApagar(linha.daConta)"
                  />
                </div>
              </BaseTableCell>
            </BaseTableRow>
          </template>
        </BaseTable>
        <p
          v-else
          class="pt-1 pb-4 mb-0 text-sm text-n-slate-11"
          data-nenhum-da-conta
        >
          {{ t('PAPEIS.GRUPOS.NENHUM_DA_CONTA') }}
        </p>
      </section>
      <section class="flex flex-col pt-6" data-da-powerbizz>
        <h2 class="mb-0 text-heading-2 text-n-slate-12">
          {{ t('PAPEIS.GRUPOS.DA_POWERBIZZ') }}
        </h2>
        <BaseTable :headers="cabecalhosDaPowerbizz" :items="linhasDaPowerbizz">
          <template #header-1="{ header }">
            <span class="block text-end">{{ header }}</span>
          </template>
          <template #row="{ items }">
            <BaseTableRow
              v-for="linha in items"
              :key="linha.chave"
              :item="linha"
              :data-grupo="linha.chave"
            >
              <BaseTableCell>
                <div class="flex flex-col min-w-0 gap-0.5">
                  <span class="text-n-slate-12">
                    {{
                      t('PAPEIS.GRUPOS.NOME', {
                        modulo: linha.modulo.rotulo,
                        nome: linha.nome,
                      })
                    }}
                  </span>
                  <span
                    v-if="linha.frase"
                    class="text-label-small text-n-slate-11"
                  >
                    {{ linha.frase }}
                  </span>
                </div>
              </BaseTableCell>
              <BaseTableCell align="end" class="tabular-nums" data-escopos>
                {{ linha.escopos }}
              </BaseTableCell>
            </BaseTableRow>
          </template>
        </BaseTable>
      </section>
    </template>

    <DialogoDoGrupo ref="dialogoDoGrupo" @salvo="carregar" />

    <Dialog
      ref="dialogoDeApagar"
      type="alert"
      :title="
        t('PAPEIS.GRUPOS.APAGAR_TITULO', { nome: paraApagar?.nome || '' })
      "
      :description="t('PAPEIS.GRUPOS.APAGAR_DESCRICAO')"
      :confirm-button-label="t('PAPEIS.GRUPOS.APAGAR_CONFIRMAR')"
      :is-loading="apagando"
      :disable-confirm-button="Boolean(erroAoApagar)"
      @confirm="apagar"
    >
      <p
        v-if="erroAoApagar"
        role="alert"
        class="flex items-start gap-2 mb-0 text-sm text-n-slate-12"
        data-erro-ao-apagar
      >
        <span
          class="i-lucide-circle-alert size-4 mt-0.5 shrink-0 text-n-ruby-11"
          aria-hidden="true"
        />
        {{ erroAoApagar }}
      </p>
    </Dialog>
  </SettingsLayout>
</template>
